import { FC, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackPromoImpressions, trackPromoClick } from '../helpers/sbSharedFeaturedTracking';

// components
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { FormattedMessage } from 'dibs-react-intl';
import { SbSharedL1FeaturedTile } from '../SbSharedL1FeaturedTile/SbSharedL1FeaturedTile';

// types
import { SbSharedFeaturedCollection_itemSearch$key as ItemSearch } from './__generated__/SbSharedFeaturedCollection_itemSearch.graphql';

type Props = {
    itemSearch: ItemSearch;
    fullWidth?: boolean;
    position?: number;
};

const itemSearchFragment = graphql`
    fragment SbSharedFeaturedCollection_itemSearch on ItemSearchQueryConnection {
        featuredCollection {
            linkData {
                text
                ...SeoLink_linkData
            }
            imageUrl
        }
    }
`;

const CREATIVE_LABEL = 'L1 collections';

export const SbSharedFeaturedCollection: FC<Props> = ({
    itemSearch: itemSearchRef,
    fullWidth = false,
    position,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { linkData, imageUrl } = itemSearch?.featuredCollection || {};
    const { text } = linkData || {};

    useEffect(() => {
        if (text && imageUrl) {
            trackPromoImpressions([CREATIVE_LABEL], position);
        }
    }, [text, imageUrl, position]);

    if (!text || !imageUrl || !linkData) {
        return null;
    }

    return (
        <SbSharedL1FeaturedTile
            fullWidth={fullWidth}
            mainImage={imageUrl}
            heading={
                <FormattedMessage
                    id="SbSharedFeaturedCollection.heading"
                    defaultMessage="collections"
                />
            }
            subHeading={text}
        >
            {({ className, children }) => (
                <SeoLink
                    className={className}
                    linkData={linkData}
                    onClick={() => trackPromoClick(CREATIVE_LABEL, position)}
                >
                    {children}
                </SeoLink>
            )}
        </SbSharedL1FeaturedTile>
    );
};
